import * as THREE from 'three';

/** Restrained earth colors shared by the first-century set-dressing factories. */
export interface FirstCenturyPalette {
  limestone: number;
  darkStone: number;
  wood: number;
  clay: number;
  clayLight: number;
  linen: number;
  wool: number;
  reed: number;
  bronze: number;
  charcoal: number;
  ember: number;
  fish: number;
  romanRed: number;
}

export const DEFAULT_FIRST_CENTURY_PALETTE: Readonly<FirstCenturyPalette> = {
  limestone: 0x8d8270,
  darkStone: 0x4f4a42,
  wood: 0x65452f,
  clay: 0x9a5738,
  clayLight: 0xb8744c,
  linen: 0xc5b9a1,
  wool: 0x796552,
  reed: 0xa28755,
  bronze: 0x756044,
  charcoal: 0x171615,
  ember: 0xd15b2f,
  fish: 0x8a806d,
  romanRed: 0x7a3028,
};

function palette(overrides: Partial<FirstCenturyPalette> = {}): FirstCenturyPalette {
  return { ...DEFAULT_FIRST_CENTURY_PALETTE, ...overrides };
}

function colorVariation(color: number, multiplier: number): number {
  return new THREE.Color(color).multiplyScalar(multiplier).getHex();
}

function standard(
  color: number,
  roughness = 0.94,
  metalness = 0,
  emissive?: number,
): THREE.MeshStandardMaterial {
  const parameters: THREE.MeshStandardMaterialParameters = {
    color,
    roughness,
    metalness,
    flatShading: true,
    emissiveIntensity: emissive === undefined ? 0 : 1.35,
  };

  // Three.js warns when a known material parameter is explicitly supplied as
  // `undefined`, so omit it entirely for ordinary non-emissive props.
  if (emissive !== undefined) parameters.emissive = emissive;

  return new THREE.MeshStandardMaterial(parameters);
}

function mesh(
  name: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  castShadow = true,
  receiveShadow = true,
): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = castShadow;
  result.receiveShadow = receiveShadow;
  return result;
}

function addCushion(
  root: THREE.Group,
  name: string,
  position: THREE.Vector3,
  rotationY: number,
  color: number,
): void {
  const cushion = mesh(
    name,
    new THREE.SphereGeometry(0.5, 14, 7),
    standard(color, 1),
  );
  cushion.scale.set(1.28, 0.22, 0.72);
  cushion.position.copy(position);
  cushion.rotation.y = rotationY;
  root.add(cushion);
}

/** A low triclinium-inspired meal setting, kept intimate rather than palatial. */
export function createLowDiningTableAndCushions(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:dining-table-and-cushions';

  const top = mesh(
    'prop:low-dining-table:top',
    new THREE.BoxGeometry(3.8, 0.14, 1.65, 4, 1, 2),
    standard(colors.wood, 0.97),
  );
  top.position.y = 0.5;
  root.add(top);

  const legMaterial = standard(colorVariation(colors.wood, 0.82), 0.98);
  for (const [index, x] of [-1.55, 1.55].entries()) {
    for (const z of [-0.6, 0.6]) {
      const leg = mesh(
        `prop:low-dining-table:leg:${index}:${z > 0 ? 'near' : 'far'}`,
        new THREE.BoxGeometry(0.22, 0.46, 0.22),
        legMaterial,
      );
      leg.position.set(x, 0.25, z);
      root.add(leg);
    }
  }

  const runner = mesh(
    'prop:low-dining-table:runner',
    new THREE.BoxGeometry(2.65, 0.018, 0.72),
    standard(colors.linen, 1),
    false,
  );
  runner.position.set(0, 0.58, 0);
  root.add(runner);

  addCushion(root, 'prop:dining-cushion:north-west', new THREE.Vector3(-1.25, 0.13, -1.35), 0.08, colors.wool);
  addCushion(root, 'prop:dining-cushion:north-east', new THREE.Vector3(0.35, 0.13, -1.35), -0.08, colorVariation(colors.wool, 1.08));
  addCushion(root, 'prop:dining-cushion:east', new THREE.Vector3(2.35, 0.13, 0), Math.PI / 2, colors.wool);
  addCushion(root, 'prop:dining-cushion:south-east', new THREE.Vector3(1.1, 0.13, 1.35), 0.08, colorVariation(colors.wool, 0.92));
  addCushion(root, 'prop:dining-cushion:south-west', new THREE.Vector3(-0.55, 0.13, 1.35), -0.08, colors.wool);
  addCushion(root, 'prop:dining-cushion:west', new THREE.Vector3(-2.35, 0.13, 0), Math.PI / 2, colorVariation(colors.wool, 1.06));

  return root;
}

/** Small clay olive-oil lamp with a wick and optional emissive flame. */
export function createOliveOilLamp(
  lit = true,
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:olive-oil-lamp';

  const bowl = mesh(
    'prop:oil-lamp:bowl',
    new THREE.SphereGeometry(0.19, 16, 8),
    standard(colors.clay, 0.98),
  );
  bowl.scale.y = 0.46;
  bowl.position.y = 0.08;

  const lid = mesh(
    'prop:oil-lamp:lid',
    new THREE.CylinderGeometry(0.13, 0.17, 0.035, 16),
    standard(colors.clayLight, 0.98),
  );
  lid.position.y = 0.155;

  const spout = mesh(
    'prop:oil-lamp:spout',
    new THREE.ConeGeometry(0.075, 0.3, 10),
    standard(colors.clay, 0.98),
  );
  spout.rotation.z = -Math.PI / 2;
  spout.position.set(0.24, 0.09, 0);

  const wick = mesh(
    'prop:oil-lamp:wick',
    new THREE.CylinderGeometry(0.012, 0.016, 0.09, 7),
    standard(colors.charcoal, 1),
  );
  wick.position.set(0.385, 0.15, 0);
  wick.rotation.z = -0.28;
  root.add(bowl, lid, spout, wick);

  if (lit) {
    const flame = mesh(
      'prop:oil-lamp:flame',
      new THREE.ConeGeometry(0.038, 0.13, 9),
      standard(0xf0b04b, 0.62, 0, 0xe06b26),
      false,
      false,
    );
    flame.position.set(0.405, 0.255, 0);
    root.add(flame);
  }

  return root;
}

/** Wheel-thrown storage jar with restrained loop handles. */
export function createClayJar(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:clay-jar';

  const profile = [
    new THREE.Vector2(0.12, 0),
    new THREE.Vector2(0.28, 0.08),
    new THREE.Vector2(0.36, 0.32),
    new THREE.Vector2(0.34, 0.74),
    new THREE.Vector2(0.22, 0.98),
    new THREE.Vector2(0.13, 1.08),
    new THREE.Vector2(0.13, 1.2),
  ];
  const body = mesh('prop:clay-jar:body', new THREE.LatheGeometry(profile, 18), standard(colors.clay, 0.99));
  root.add(body);

  const rim = mesh(
    'prop:clay-jar:rim',
    new THREE.TorusGeometry(0.14, 0.025, 7, 18),
    standard(colors.clayLight, 0.98),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 1.2;
  root.add(rim);

  for (const side of [-1, 1]) {
    const handle = mesh(
      `prop:clay-jar:handle:${side < 0 ? 'left' : 'right'}`,
      new THREE.TorusGeometry(0.17, 0.026, 7, 12, Math.PI * 1.25),
      standard(colorVariation(colors.clay, 0.9), 0.99),
    );
    handle.position.set(side * 0.29, 0.91, 0);
    handle.rotation.set(0, side < 0 ? Math.PI : 0, Math.PI / 2);
    root.add(handle);
  }
  return root;
}

/** Flat woven reed mat suitable for floors, tombs, and shoreline scenes. */
export function createWovenMat(
  width = 2.3,
  depth = 1.35,
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:woven-mat';
  const stripCount = 16;
  const stripWidth = width / stripCount;
  for (let index = 0; index < stripCount; index += 1) {
    const strip = mesh(
      `prop:woven-mat:strip:${index}`,
      new THREE.BoxGeometry(stripWidth * 0.93, 0.025, depth),
      standard(colorVariation(colors.reed, index % 2 === 0 ? 0.92 : 1.08), 1),
      false,
    );
    strip.position.set(-width / 2 + stripWidth * (index + 0.5), 0.018, 0);
    root.add(strip);
  }
  for (const z of [-depth / 2, depth / 2]) {
    const binding = mesh(
      `prop:woven-mat:binding:${z < 0 ? 'north' : 'south'}`,
      new THREE.BoxGeometry(width + 0.08, 0.035, 0.055),
      standard(colorVariation(colors.reed, 0.72), 1),
      false,
    );
    binding.position.set(0, 0.035, z);
    root.add(binding);
  }
  return root;
}

/** Open woven basket for bread, fish, linen, or market dressing. */
export function createWovenBasket(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:woven-basket';

  const body = mesh(
    'prop:woven-basket:body',
    new THREE.CylinderGeometry(0.36, 0.29, 0.38, 18, 3, true),
    standard(colors.reed, 1),
  );
  body.position.y = 0.22;
  root.add(body);

  for (let index = 0; index < 4; index += 1) {
    const band = mesh(
      `prop:woven-basket:band:${index}`,
      new THREE.TorusGeometry(0.3 + index * 0.016, 0.018, 6, 18),
      standard(colorVariation(colors.reed, index % 2 === 0 ? 0.76 : 0.9), 1),
    );
    band.rotation.x = Math.PI / 2;
    band.position.y = 0.08 + index * 0.1;
    root.add(band);
  }

  const base = mesh(
    'prop:woven-basket:base',
    new THREE.CircleGeometry(0.29, 18),
    standard(colorVariation(colors.reed, 0.78), 1),
  );
  base.rotation.x = -Math.PI / 2;
  base.position.y = 0.035;
  root.add(base);
  return root;
}

/** Charcoal cooking brazier with a simple grill and two prepared fish. */
export function createCharcoalBrazierWithFish(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:charcoal-brazier-with-fish';

  const bowl = mesh(
    'prop:charcoal-brazier:bowl',
    new THREE.CylinderGeometry(0.46, 0.31, 0.2, 20, 2, false),
    standard(colors.clay, 0.98),
  );
  bowl.position.y = 0.22;
  root.add(bowl);

  for (let index = 0; index < 9; index += 1) {
    const angle = index * 2.399;
    const radius = 0.08 + (index % 3) * 0.08;
    const coal = mesh(
      `prop:charcoal-brazier:coal:${index}`,
      new THREE.IcosahedronGeometry(0.07 + (index % 2) * 0.018, 1),
      standard(index % 3 === 0 ? colorVariation(colors.ember, 0.72) : colors.charcoal, 1, 0, index % 3 === 0 ? colors.ember : undefined),
    );
    coal.position.set(Math.cos(angle) * radius, 0.37 + (index % 2) * 0.018, Math.sin(angle) * radius);
    root.add(coal);
  }

  // Small irregular flame bodies avoid the graphic "row of triangles" look
  // while preserving a restrained practical fire around the visible fish.
  for (let index = 0; index < 3; index += 1) {
    const flame = mesh(
      `prop:charcoal-brazier:flame:${index}`,
      new THREE.SphereGeometry(0.085, 10, 7),
      standard(index === 1 ? 0xffb34f : 0xe96d31, 0.58, 0, index === 1 ? 0xff9a32 : 0xd84f24),
      false,
      false,
    );
    flame.position.set((index - 1) * 0.13, 0.52 + (index % 2) * 0.035, index === 1 ? 0.02 : -0.04);
    flame.scale.set(0.62, 1.18 - index * 0.08, 0.68);
    root.add(flame);
  }

  const iron = standard(0x292725, 0.78, 0.18);
  for (const z of [-0.19, -0.06, 0.07, 0.2]) {
    const bar = mesh('prop:charcoal-brazier:grill-bar', new THREE.CylinderGeometry(0.012, 0.012, 0.82, 7), iron);
    bar.rotation.z = Math.PI / 2;
    bar.position.set(0, 0.48, z);
    root.add(bar);
  }

  for (const [index, z] of [-0.12, 0.13].entries()) {
    const fish = new THREE.Group();
    fish.name = `prop:charcoal-brazier:fish:${index}`;
    const body = mesh('body', new THREE.SphereGeometry(0.15, 12, 7), standard(colors.fish, 0.92));
    body.scale.set(1.75, 0.38, 0.7);
    const tail = mesh('tail', new THREE.ConeGeometry(0.09, 0.18, 6), standard(colorVariation(colors.fish, 0.88), 0.94));
    tail.rotation.z = Math.PI / 2;
    tail.position.x = -0.31;
    fish.position.set(index === 0 ? -0.08 : 0.08, 0.57, z);
    fish.rotation.y = index === 0 ? 0.08 : Math.PI + 0.08;
    fish.add(body, tail);
    root.add(fish);
  }
  return root;
}

/** Stone burial bench, wrapped body, folded linen, and a separate face cloth. */
export function createTombBierAndLinen(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:tomb-bier-and-linen';

  const bier = mesh(
    'prop:tomb-bier:stone',
    new THREE.BoxGeometry(2.65, 0.34, 0.86, 3, 1, 2),
    standard(colors.darkStone, 1),
  );
  bier.position.y = 0.32;

  // Build the shroud around a recognisable human contour. A single extruded
  // outline looked like a white sarcophagus from the burial cameras; these
  // overlapping volumes preserve head, shoulder, torso, hip, leg, and foot
  // landmarks while keeping every surface solemnly covered.
  const body = new THREE.Group();
  body.name = 'prop:tomb-bier:wrapped-body';
  const bodyLinen = new THREE.MeshStandardMaterial({
    color: colorVariation(colors.linen, 0.88),
    roughness: 1,
    metalness: 0,
    flatShading: false,
  });
  const shadowLinen = new THREE.MeshStandardMaterial({
    color: colorVariation(colors.linen, 0.72),
    roughness: 1,
    metalness: 0,
    flatShading: false,
  });
  const highlightLinen = new THREE.MeshStandardMaterial({
    color: colorVariation(colors.linen, 0.98),
    roughness: 1,
    metalness: 0,
    flatShading: false,
    side: THREE.DoubleSide,
  });

  // One continuous elliptical shell carries the full shoulder-to-foot
  // silhouette. Earlier separate capsules telegraphed their primitive seams
  // through the top sheet; this tapered envelope keeps a human chest, pelvis,
  // knees and feet while reading as one weighted shroud.
  const bodyRings: Array<{ x: number; height: number; width: number; centerY: number }> = [
    { x: 0.6, height: 0.105, width: 0.14, centerY: -0.015 },
    { x: 0.44, height: 0.2, width: 0.32, centerY: -0.018 },
    { x: 0.16, height: 0.185, width: 0.305, centerY: -0.02 },
    { x: -0.2, height: 0.17, width: 0.285, centerY: -0.025 },
    { x: -0.46, height: 0.145, width: 0.235, centerY: -0.032 },
    { x: -0.7, height: 0.12, width: 0.17, centerY: -0.045 },
    { x: -0.9, height: 0.095, width: 0.135, centerY: -0.055 },
    { x: -1.01, height: 0.07, width: 0.09, centerY: -0.06 },
  ];
  const ringSegments = 24;
  const bodyVertices: number[] = [];
  const bodyIndices: number[] = [];
  for (const ring of bodyRings) {
    for (let segment = 0; segment < ringSegments; segment += 1) {
      const angle = segment / ringSegments * Math.PI * 2;
      bodyVertices.push(
        ring.x,
        ring.centerY + Math.sin(angle) * ring.height,
        Math.cos(angle) * ring.width,
      );
    }
  }
  for (let ringIndex = 0; ringIndex < bodyRings.length - 1; ringIndex += 1) {
    const current = ringIndex * ringSegments;
    const next = (ringIndex + 1) * ringSegments;
    for (let segment = 0; segment < ringSegments; segment += 1) {
      const following = (segment + 1) % ringSegments;
      bodyIndices.push(
        current + segment, next + segment, next + following,
        current + segment, next + following, current + following,
      );
    }
  }
  const continuousGeometry = new THREE.BufferGeometry();
  continuousGeometry.setAttribute('position', new THREE.Float32BufferAttribute(bodyVertices, 3));
  continuousGeometry.setIndex(bodyIndices);
  continuousGeometry.computeVertexNormals();
  const continuousShroud = mesh('prop:tomb-bier:continuous-body-shroud', continuousGeometry, bodyLinen);
  body.add(continuousShroud);

  const shoulders = mesh(
    'prop:tomb-bier:shoulders',
    new THREE.SphereGeometry(0.33, 20, 12),
    bodyLinen,
  );
  shoulders.scale.set(1.12, 0.68, 1.03);
  shoulders.position.set(0.46, 0.01, 0);

  const torso = mesh(
    'prop:tomb-bier:torso-wrap',
    new THREE.CapsuleGeometry(0.285, 0.58, 10, 20),
    bodyLinen,
  );
  torso.rotation.z = Math.PI / 2;
  torso.scale.set(1, 0.98, 1.04);
  torso.position.set(0.12, -0.01, 0);

  const pelvis = mesh(
    'prop:tomb-bier:pelvis-wrap',
    new THREE.SphereGeometry(0.29, 18, 11),
    bodyLinen,
  );
  pelvis.scale.set(1.04, 0.7, 1.02);
  pelvis.position.set(-0.36, -0.035, 0);

  // A narrower neck bridge prevents the head and shoulders reading as two
  // unrelated bundles from the high burial camera. It remains entirely linen
  // covered while preserving the unmistakable taper of a human body.
  const neck = mesh(
    'prop:tomb-bier:neck-wrap',
    new THREE.CapsuleGeometry(0.155, 0.11, 8, 16),
    shadowLinen,
  );
  neck.rotation.z = Math.PI / 2;
  neck.scale.set(1, 0.88, 0.94);
  neck.position.set(0.7, 0.015, 0);

  // Wrapped forearms rest along the ribcage. Their low, asymmetrical crown is
  // enough to imply the body's weight beneath the sheet without exposing skin
  // or creating a sculptural sarcophagus silhouette.
  for (const [index, z] of [-0.245, 0.245].entries()) {
    const forearm = mesh(
      `prop:tomb-bier:forearm-wrap:${index}`,
      new THREE.CapsuleGeometry(0.095, 0.46, 8, 14),
      index === 0 ? shadowLinen : bodyLinen,
    );
    forearm.rotation.z = Math.PI / 2;
    forearm.rotation.y = index === 0 ? -0.055 : 0.045;
    forearm.scale.set(1, 0.82, 0.86);
    forearm.position.set(0.05, 0.11, z);
    // The contour is baked into the continuous shell above; retain this local
    // construction description without exposing a capsule seam in the render.
  }

  const legMaterial = bodyLinen.clone();
  legMaterial.color.setHex(colorVariation(colors.linen, 0.84));
  for (const [index, z] of [-0.115, 0.115].entries()) {
    const leg = mesh(
      `prop:tomb-bier:leg-wrap:${index}`,
      new THREE.CapsuleGeometry(0.145, 0.48, 9, 16),
      legMaterial,
    );
    leg.rotation.z = Math.PI / 2;
    leg.scale.set(1, 0.9, 0.94);
    leg.position.set(-0.72, -0.065, z);
    const foot = mesh(
      `prop:tomb-bier:foot-wrap:${index}`,
      new THREE.CapsuleGeometry(0.11, 0.19, 8, 14),
      shadowLinen,
    );
    foot.rotation.z = Math.PI / 2;
    foot.scale.set(1, 0.72, 0.9);
    foot.position.set(-1.04, -0.08, z);
    const knee = mesh(
      `prop:tomb-bier:knee-wrap:${index}`,
      new THREE.SphereGeometry(0.155, 14, 9),
      index === 0 ? bodyLinen : legMaterial,
    );
    knee.scale.set(0.82, 0.68, 0.9);
    knee.position.set(-0.58, -0.005, z);
  }

  // Narrow winding bands create linen rhythm without becoming bright rings.
  const bandPositions = [0.45, 0.25, 0.03, -0.2, -0.44, -0.66, -0.86];
  for (const [index, x] of bandPositions.entries()) {
    const radius = x > 0.34 ? 0.305 : x > -0.32 ? 0.275 : x > -0.58 ? 0.215 : 0.145;
    const crown = x > 0.34 ? 0.205 : x > -0.32 ? 0.18 : x > -0.58 ? 0.145 : 0.1;
    const band = mesh(
      `prop:tomb-bier:winding-band:${index}`,
      new THREE.PlaneGeometry(0.026 + (index % 2) * 0.007, radius * 1.82, 1, 4),
      index % 2 === 0 ? highlightLinen : shadowLinen,
      false,
    );
    band.rotation.x = -Math.PI / 2;
    band.rotation.z = (index % 2 ? -1 : 1) * 0.035;
    band.position.set(x, crown + 0.004, 0);
    body.add(band);
  }

  // A hand-laid outer sheet follows the body crown and carries quiet folds.
  const sheetGeometry = new THREE.PlaneGeometry(1.72, 0.66, 20, 8);
  const sheetPositions = sheetGeometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < sheetPositions.count; index += 1) {
    const x = sheetPositions.getX(index);
    const zAcross = sheetPositions.getY(index);
    const legTaper = THREE.MathUtils.smoothstep(x, -0.84, -0.26);
    const neckTaper = 1 - THREE.MathUtils.smoothstep(x, 0.42, 0.86);
    const widthProfile = THREE.MathUtils.lerp(0.48, 1, legTaper) * THREE.MathUtils.lerp(0.46, 1, neckTaper);
    const shapedAcross = zAcross * widthProfile;
    const shoulderRise = Math.exp(-Math.pow((x - 0.42) / 0.42, 2)) * 0.12;
    const torsoRise = Math.exp(-Math.pow((x + 0.02) / 0.78, 2)) * 0.08;
    const fold = Math.sin((x + 1.05) * 17 + shapedAcross * 6) * 0.012;
    const normalizedEdge = Math.min(1, Math.abs(zAcross) / 0.33);
    const edgeDrape = Math.pow(normalizedEdge, 2.4) * (0.035 + (1 - widthProfile) * 0.025);
    sheetPositions.setY(index, shapedAcross);
    sheetPositions.setZ(index, shoulderRise + torsoRise + fold - edgeDrape);
  }
  sheetGeometry.computeVertexNormals();
  const outerSheet = mesh('prop:tomb-bier:outer-sheet', sheetGeometry, highlightLinen, false);
  outerSheet.rotation.x = -Math.PI / 2;
  outerSheet.position.set(-0.02, 0.17, 0);

  body.add(outerSheet);
  body.position.set(-0.08, 0.72, 0);

  const headWrap = new THREE.Group();
  headWrap.name = 'prop:tomb-bier:head-wrap';
  const wrappedHead = mesh(
    'prop:tomb-bier:wrapped-head-volume',
    new THREE.SphereGeometry(0.235, 20, 14),
    bodyLinen,
  );
  wrappedHead.scale.set(1.02, 1.08, 0.9);
  const faceBand = mesh(
    'prop:tomb-bier:face-band',
    new THREE.CylinderGeometry(0.242, 0.242, 0.075, 20, 1, true),
    shadowLinen,
  );
  faceBand.rotation.z = Math.PI / 2;
  faceBand.position.x = 0.035;
  const crownFold = mesh(
    'prop:tomb-bier:crown-fold',
    new THREE.SphereGeometry(0.245, 20, 10, 0, Math.PI * 2, 0, Math.PI * 0.45),
    highlightLinen,
  );
  crownFold.scale.set(1.03, 1.08, 0.92);
  crownFold.position.y = 0.01;
  // A tiny linen-covered nasal ridge establishes that the head is supine,
  // eliminating the last rounded-bundle ambiguity in overhead views.
  const faceContour = mesh(
    'prop:tomb-bier:face-contour',
    new THREE.ConeGeometry(0.052, 0.095, 10, 2),
    highlightLinen,
    false,
  );
  faceContour.position.set(0.03, 0.24, 0.008);
  faceContour.scale.set(0.7, 0.72, 0.9);
  faceContour.rotation.z = -0.08;
  const chinFold = mesh(
    'prop:tomb-bier:chin-fold',
    new THREE.TorusGeometry(0.13, 0.012, 5, 18, Math.PI * 1.15),
    shadowLinen,
    false,
  );
  chinFold.rotation.x = Math.PI / 2;
  chinFold.rotation.z = -Math.PI * 0.58;
  chinFold.position.set(-0.13, 0.12, 0);
  headWrap.add(wrappedHead, faceBand, crownFold, faceContour, chinFold);
  headWrap.position.set(0.8, 0.77, 0);

  const foldedLinen = mesh(
    'prop:tomb-bier:folded-linen',
    new THREE.BoxGeometry(0.62, 0.1, 0.48, 3, 1, 2),
    standard(colorVariation(colors.linen, 1.06), 1),
  );
  foldedLinen.position.set(-0.84, 0.6, -0.72);
  foldedLinen.rotation.y = 0.12;

  const faceCloth = mesh(
    'prop:tomb-bier:face-cloth',
    new THREE.CircleGeometry(0.22, 14),
    standard(colorVariation(colors.linen, 0.86), 1),
    false,
  );
  faceCloth.rotation.x = -Math.PI / 2;
  faceCloth.position.set(0.9, 0.57, -0.67);

  const stoneCourseMaterial = standard(colorVariation(colors.darkStone, 1.16), 1);
  for (const [index, x] of [-0.92, 0, 0.92].entries()) {
    const course = mesh(
      `prop:tomb-bier:stone-course:${index}`,
      new THREE.BoxGeometry(0.78, 0.12, 0.91, 2, 1, 2),
      stoneCourseMaterial,
    );
    course.position.set(x, 0.1, 0);
    root.add(course);
  }

  const burialJar = createClayJar({ clay: colorVariation(colors.clay, 0.72) });
  burialJar.name = 'prop:tomb-bier:burial-jar';
  burialJar.scale.setScalar(0.34);
  burialJar.position.set(-1.08, 0.03, -0.78);
  const spiceBowl = mesh(
    'prop:tomb-bier:spice-bowl',
    new THREE.CylinderGeometry(0.18, 0.12, 0.09, 16, 1, true),
    standard(colorVariation(colors.clayLight, 0.78), 0.98),
  );
  spiceBowl.position.set(-0.6, 0.07, -0.72);

  root.add(bier, body, headWrap, foldedLinen, faceCloth, burialJar, spiceBowl);
  return root;
}

/** Roman bema, restrained judgment seat, and a single vexillum-style standard. */
export function createRomanJudgmentSeatAndStandard(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const colors = palette(overrides);
  const root = new THREE.Group();
  root.name = 'first-century:roman-judgment-seat-and-standard';

  const stepWidths = [4.8, 4.05, 3.3];
  for (const [index, width] of stepWidths.entries()) {
    const step = mesh(
      `prop:roman-bema:step:${index}`,
      new THREE.BoxGeometry(width, 0.28, 2.45 - index * 0.28, 4, 1, 2),
      standard(colorVariation(colors.limestone, 0.82 + index * 0.08), 0.99),
    );
    step.position.set(0, 0.14 + index * 0.28, -index * 0.16);
    root.add(step);
  }

  const seat = new THREE.Group();
  seat.name = 'prop:roman-judgment-seat';
  seat.position.set(0, 0.84, -0.42);
  const seatStone = standard(colorVariation(colors.limestone, 0.92), 0.99);
  const bench = mesh('prop:roman-seat:bench', new THREE.BoxGeometry(1.25, 0.18, 0.58), seatStone);
  bench.position.y = 0.52;
  const back = mesh('prop:roman-seat:back', new THREE.BoxGeometry(1.12, 0.92, 0.16), seatStone);
  back.position.set(0, 0.95, -0.23);
  for (const side of [-1, 1]) {
    const support = mesh('prop:roman-seat:support', new THREE.BoxGeometry(0.2, 0.52, 0.5), seatStone);
    support.position.set(side * 0.48, 0.25, 0);
    seat.add(support);
    const arm = mesh('prop:roman-seat:arm', new THREE.BoxGeometry(0.2, 0.15, 0.58), seatStone);
    arm.position.set(side * 0.58, 0.74, 0.02);
    seat.add(arm);
  }
  seat.add(bench, back);
  root.add(seat);

  const standardRoot = new THREE.Group();
  standardRoot.name = 'prop:roman-standard';
  standardRoot.position.set(2.35, 0.55, -0.55);
  const bronze = standard(colors.bronze, 0.68, 0.34);
  const pole = mesh('prop:roman-standard:pole', new THREE.CylinderGeometry(0.035, 0.045, 3.25, 9), bronze);
  pole.position.y = 1.62;
  const crossbar = mesh('prop:roman-standard:crossbar', new THREE.CylinderGeometry(0.035, 0.035, 1.05, 9), bronze);
  crossbar.rotation.z = Math.PI / 2;
  crossbar.position.y = 2.65;
  const cloth = mesh(
    'prop:roman-standard:vexillum',
    new THREE.PlaneGeometry(0.86, 0.72, 2, 2),
    new THREE.MeshStandardMaterial({ color: colors.romanRed, roughness: 1, metalness: 0, flatShading: true, side: THREE.DoubleSide }),
  );
  cloth.position.set(0, 2.23, 0);
  const disk = mesh('prop:roman-standard:disk', new THREE.CylinderGeometry(0.13, 0.13, 0.035, 14), bronze);
  disk.rotation.x = Math.PI / 2;
  disk.position.set(0, 2.25, 0.025);
  standardRoot.add(pole, crossbar, cloth, disk);
  root.add(standardRoot);

  return root;
}

/** Convenience gallery containing one of every prop family for inspection. */
export function createFirstCenturyPropGallery(
  overrides: Partial<FirstCenturyPalette> = {},
): THREE.Group {
  const root = new THREE.Group();
  root.name = 'first-century:prop-gallery';
  const dining = createLowDiningTableAndCushions(overrides);
  dining.position.set(-3.2, 0, -1.8);
  const lamp = createOliveOilLamp(true, overrides);
  lamp.position.set(-0.8, 0, 0.2);
  const jar = createClayJar(overrides);
  jar.position.set(0, 0, 0.2);
  const mat = createWovenMat(2.3, 1.35, overrides);
  mat.position.set(1.9, 0, 0.35);
  const basket = createWovenBasket(overrides);
  basket.position.set(3.4, 0, 0.3);
  const brazier = createCharcoalBrazierWithFish(overrides);
  brazier.position.set(-2.2, 0, 2.2);
  const tomb = createTombBierAndLinen(overrides);
  tomb.position.set(0.4, 0, 2.5);
  const judgment = createRomanJudgmentSeatAndStandard(overrides);
  judgment.position.set(4.8, 0, 1.7);
  root.add(dining, lamp, jar, mat, basket, brazier, tomb, judgment);
  return root;
}
